import React, { useRef, useState } from 'react';
import { Button } from 'react-bootstrap';

/**
 * A drop-in replacement for react-bootstrap's <Button> that prevents
 * accidental double-clicks.
 *
 * While the (possibly async) onClick handler is running, the button is
 * disabled. A synchronous ref guard also blocks any duplicate clicks that
 * fire before React has a chance to re-render - so rapid repeated clicks
 * never trigger the action more than once.
 *
 * Usage:
 *   <AsyncButton onClick={async () => { await save(); }}>שמור</AsyncButton>
 *
 * Optional props:
 *   - loadingText: text/element shown while the action runs (defaults to children)
 */
const AsyncButton = ({ onClick, disabled, children, loadingText, ...rest }) => {
  const [loading, setLoading] = useState(false);
  const inFlight = useRef(false);

  const handleClick = async (event) => {
    if (inFlight.current || loading) return;
    if (typeof onClick !== 'function') return;

    inFlight.current = true;
    setLoading(true);
    try {
      await onClick(event);
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  };

  return (
    <Button {...rest} disabled={disabled || loading} onClick={handleClick}>
      {loading && loadingText !== undefined ? loadingText : children}
    </Button>
  );
};

export default AsyncButton;
